// Management system for tracking manager-rep relationships and analytics
// Handles company-based associations, territory mapping, and performance metrics

import { logger } from './logger';

const MANAGER_RELATIONSHIPS_KEY = 'meetingsync_manager_relationships';
const COMPANY_DATA_KEY = 'meetingsync_company_data';
const PERFORMANCE_METRICS_KEY = 'meetingsync_performance_metrics';

export const ManagementSystem = {
  // Company and territory management
  
  // Get all company data
  getAllCompanies() {
    try {
      const companies = localStorage.getItem(COMPANY_DATA_KEY);
      return companies ? JSON.parse(companies) : [];
    } catch (error) {
      logger.error('Error getting companies:', error);
      return [];
    }
  },

  // Save company data
  saveCompany(companyData) {
    try {
      const companies = this.getAllCompanies();
      const existingIndex = companies.findIndex(c => c.id === companyData.id);
      
      if (existingIndex >= 0) {
        companies[existingIndex] = { ...companies[existingIndex], ...companyData };
      } else {
        const newCompany = {
          ...companyData,
          id: companyData.id || Date.now().toString(),
          createdAt: new Date().toISOString()
        };
        companies.push(newCompany);
      }
      
      localStorage.setItem(COMPANY_DATA_KEY, JSON.stringify(companies));
      return companies.find(c => c.id === companyData.id);
    } catch (error) {
      logger.error('Error saving company:', error);
      return null;
    }
  },

  // Manager-Rep relationship management
  
  // Get all manager relationships
  getAllRelationships() {
    try {
      const relationships = localStorage.getItem(MANAGER_RELATIONSHIPS_KEY);
      return relationships ? JSON.parse(relationships) : [];
    } catch (error) {
      logger.error('Error getting relationships:', error);
      return [];
    }
  },

  // Assign rep to manager
  assignRepToManager(repId, managerId, companyId) {
    try {
      const relationships = this.getAllRelationships();
      const existingIndex = relationships.findIndex(r => r.repId === repId);
      
      const relationship = {
        repId,
        managerId,
        companyId,
        assignedAt: new Date().toISOString(),
        status: 'active'
      };
      
      if (existingIndex >= 0) {
        relationships[existingIndex] = relationship;
      } else {
        relationships.push(relationship);
      }
      
      localStorage.setItem(MANAGER_RELATIONSHIPS_KEY, JSON.stringify(relationships));
      return relationship;
    } catch (error) {
      logger.error('Error assigning rep to manager:', error);
      return null;
    }
  },

  // Get reps for a manager
  getRepsForManager(managerId) {
    const relationships = this.getAllRelationships();
    return relationships.filter(r => r.managerId === managerId && r.status === 'active');
  },

  // Get manager for a rep
  getManagerForRep(repId) {
    const relationships = this.getAllRelationships();
    return relationships.find(r => r.repId === repId && r.status === 'active');
  },

  // Get reps by company (for auto-assignment suggestions)
  getRepsByCompany(companyId) {
    const relationships = this.getAllRelationships();
    return relationships.filter(r => r.companyId === companyId);
  },

  // Performance metrics and analytics
  
  // Calculate rep performance metrics
  calculateRepPerformance(repId, meetings, timeframe = 30) {
    const now = new Date();
    const cutoffDate = new Date(now.getTime() - (timeframe * 24 * 60 * 60 * 1000));
    
    const recentMeetings = meetings.filter(m => {
      const meetingDate = new Date(m.date);
      return meetingDate >= cutoffDate && m.salesRepId === repId;
    });

    const totalMeetings = recentMeetings.length;
    const completedMeetings = recentMeetings.filter(m => m.status === 'completed').length;
    const avgMeetingsPerWeek = (totalMeetings / timeframe) * 7;
    
    // Calculate total spending (assuming orderTotal is stored as "$X.XX")
    const totalSpent = recentMeetings.reduce((sum, m) => {
      if (m.orderTotal && m.orderTotal !== '-') {
        const amount = parseFloat(m.orderTotal.replace('$', '').replace(',', ''));
        return sum + (isNaN(amount) ? 0 : amount);
      }
      return sum;
    }, 0);

    // Get unique physicians met with
    const uniquePhysicians = new Set(recentMeetings.map(m => m.customerId)).size;
    const costPerPhysician = uniquePhysicians > 0 ? totalSpent / uniquePhysicians : 0;
    const avgCostPerMeeting = totalMeetings > 0 ? totalSpent / totalMeetings : 0;

    return {
      repId,
      timeframe,
      totalMeetings,
      completedMeetings,
      completionRate: totalMeetings > 0 ? (completedMeetings / totalMeetings) * 100 : 0,
      avgMeetingsPerWeek: Math.round(avgMeetingsPerWeek * 10) / 10,
      totalSpent,
      uniquePhysicians,
      costPerPhysician: Math.round(costPerPhysician * 100) / 100,
      avgCostPerMeeting: Math.round(avgCostPerMeeting * 100) / 100,
      calculatedAt: new Date().toISOString()
    };
  },

  // Calculate team performance for a manager
  calculateTeamPerformance(managerId, allMeetings, allUsers) {
    const teamReps = this.getRepsForManager(managerId);
    const teamPerformance = [];
    
    let totalTeamMeetings = 0;
    let totalTeamSpending = 0;
    let totalTeamPhysicians = new Set();

    teamReps.forEach(relationship => {
      const repUser = allUsers.find(u => u.id === relationship.repId);
      if (repUser) {
        const repMeetings = allMeetings.filter(m => m.salesRepId === relationship.repId);
        const performance = this.calculateRepPerformance(relationship.repId, allMeetings);
        
        totalTeamMeetings += performance.totalMeetings;
        totalTeamSpending += performance.totalSpent;
        
        // Add unique physicians to team set
        repMeetings.forEach(m => totalTeamPhysicians.add(m.customerId));
        
        teamPerformance.push({
          ...performance,
          repName: repUser.name || 'Unknown Rep',
          repEmail: repUser.email,
          territory: repUser.territory || 'Unassigned',
          companyId: relationship.companyId
        });
      }
    });

    const teamSummary = {
      managerId,
      totalReps: teamReps.length,
      totalMeetings: totalTeamMeetings,
      totalSpending: totalTeamSpending,
      totalUniquePhysicians: totalTeamPhysicians.size,
      avgMeetingsPerRep: teamReps.length > 0 ? Math.round((totalTeamMeetings / teamReps.length) * 10) / 10 : 0,
      avgSpendingPerRep: teamReps.length > 0 ? Math.round((totalTeamSpending / teamReps.length) * 100) / 100 : 0,
      teamCostPerPhysician: totalTeamPhysicians.size > 0 ? Math.round((totalTeamSpending / totalTeamPhysicians.size) * 100) / 100 : 0,
      calculatedAt: new Date().toISOString()
    };

    return {
      teamSummary,
      repPerformances: teamPerformance.sort((a, b) => b.totalMeetings - a.totalMeetings)
    };
  },

  // Territory analysis
  analyzeTerritory(managerId, allUsers, allMeetings) {
    const teamReps = this.getRepsForManager(managerId);
    const territoryData = {};

    teamReps.forEach(relationship => {
      const repUser = allUsers.find(u => u.id === relationship.repId);
      if (repUser && repUser.territory) {
        if (!territoryData[repUser.territory]) {
          territoryData[repUser.territory] = {
            territory: repUser.territory,
            reps: [],
            totalMeetings: 0,
            totalSpending: 0,
            uniquePhysicians: new Set()
          };
        }

        const repMeetings = allMeetings.filter(m => m.salesRepId === relationship.repId);
        const spending = repMeetings.reduce((sum, m) => {
          if (m.orderTotal && m.orderTotal !== '-') {
            const amount = parseFloat(m.orderTotal.replace('$', '').replace(',', ''));
            return sum + (isNaN(amount) ? 0 : amount);
          }
          return sum;
        }, 0);

        territoryData[repUser.territory].reps.push({
          id: repUser.id,
          name: repUser.name,
          meetings: repMeetings.length,
          spending: spending
        });
        
        territoryData[repUser.territory].totalMeetings += repMeetings.length;
        territoryData[repUser.territory].totalSpending += spending;
        
        repMeetings.forEach(m => {
          territoryData[repUser.territory].uniquePhysicians.add(m.customerId);
        });
      }
    });

    // Convert Sets to counts and sort territories by performance
    const territoryAnalysis = Object.values(territoryData).map(territory => ({
      ...territory,
      uniquePhysicians: territory.uniquePhysicians.size,
      avgMeetingsPerRep: territory.reps.length > 0 ? Math.round((territory.totalMeetings / territory.reps.length) * 10) / 10 : 0,
      costPerPhysician: territory.uniquePhysicians.size > 0 ? Math.round((territory.totalSpending / territory.uniquePhysicians.size) * 100) / 100 : 0
    })).sort((a, b) => b.totalMeetings - a.totalMeetings);

    return territoryAnalysis;
  },

  // ROI and cost effectiveness analysis
  calculateROIMetrics(teamPerformance) {
    const rois = teamPerformance.repPerformances.map(rep => ({
      repId: rep.repId,
      repName: rep.repName,
      costEfficiencyScore: rep.costPerPhysician > 0 ? Math.round((rep.totalMeetings / rep.costPerPhysician) * 100) : 0,
      activityScore: rep.avgMeetingsPerWeek * 10, // Activity multiplier
      roi: rep.totalSpent > 0 ? Math.round((rep.completedMeetings / rep.totalSpent) * 1000) : 0, // Meetings per $1000 spent
      territoryPenetration: rep.uniquePhysicians // Number of unique physician relationships
    }));

    return rois.sort((a, b) => b.roi - a.roi);
  },

  // Format currency for display
  formatCurrency(amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  },

  // Get performance trends (simple month-over-month for now)
  getPerformanceTrends(repId, allMeetings) {
    const now = new Date();
    const lastMonth = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));
    const twoMonthsAgo = new Date(now.getTime() - (60 * 24 * 60 * 60 * 1000));

    const thisMonthMeetings = allMeetings.filter(m => {
      const meetingDate = new Date(m.date);
      return meetingDate >= lastMonth && m.salesRepId === repId;
    }).length;

    const lastMonthMeetings = allMeetings.filter(m => {
      const meetingDate = new Date(m.date);
      return meetingDate >= twoMonthsAgo && meetingDate < lastMonth && m.salesRepId === repId;
    }).length;

    const trend = lastMonthMeetings > 0 ? 
      Math.round(((thisMonthMeetings - lastMonthMeetings) / lastMonthMeetings) * 100) : 
      (thisMonthMeetings > 0 ? 100 : 0);

    return {
      thisMonth: thisMonthMeetings,
      lastMonth: lastMonthMeetings,
      trendPercentage: trend,
      direction: trend > 0 ? 'up' : trend < 0 ? 'down' : 'stable'
    };
  }
};